import Negotiator from 'negotiator'
import { type ReadonlyHeaders } from 'next/dist/server/web/spec-extension/adapters/headers'
import { type NextRequest } from 'next/server'

import type { Lang, LocaleCode, LocaleConfig, LocalePath } from '@src/i18n/locales'
import { i18nConfig } from '@src/i18n/locales'
import { HEADERS } from '@src/utils/constants.util'

export function getDefaultLocale() {
  const defaultLocale = i18nConfig.defaultLocale as keyof typeof i18nConfig.locales
  return i18nConfig.locales[defaultLocale] as LocaleConfig
}

export function getLocaleFromHeaders(headers: ReadonlyHeaders) {
  const localeCode = headers.get(HEADERS.locale) ?? getDefaultLocale().code
  return getLocaleFromCode(localeCode)
}

export function getLocales() {
  return Object.values(i18nConfig.locales) as LocaleConfig[]
}

export function getLocaleCodes() {
  return Object.values(i18nConfig.locales).map((locale) => locale.code) as LocaleCode[]
}

export function getLocalePaths() {
  return Object.values(i18nConfig.locales).map((locale) => locale.path) as LocalePath[]
}

export function getLocaleFromCountryCode(countryCode: string) {
  return getLocales().find((locale) => locale.countryCode === countryCode) ?? getDefaultLocale()
}

/**
 * This function returns the locale for the given `countryCodeLowercase`.
 *
 * If there are multiple locales for the same country code,
 * `lang` will be used to find the matching one.
 *
 * If `lang` is ommited or if there are no matches, the first locale found for the country code will be returned.
 */
export const getLocaleForCountryCodeLang = (countryCodeLowercase: string, lang?: string) => {
  const localesForCountry = getLocales().filter(
    (l) => l.countryCodeLowercase === countryCodeLowercase
  )
  if (localesForCountry.length === 1) return localesForCountry[0]
  return localesForCountry.find((l) => l.lang === lang) ?? localesForCountry[0]
}

// ['en', 'fr']
export function getLangs() {
  const langsSet = new Set(Object.values(i18nConfig.locales).map((locale) => locale.lang))
  return Array.from(langsSet) as Array<Lang>
}

// "en-US" -> LocaleConfig
export const getLocaleFromCode = (localeCode: string) => {
  const locale = Object.values(i18nConfig.locales).find((locale) => locale.code === localeCode)
  return (locale ?? getDefaultLocale()) as LocaleConfig
}

// "/us/en/..." -> LocaleConfig
export const getLocaleFromPath = (path: string = getDefaultLocale().path) => {
  const locale = Object.values(i18nConfig.locales).find((locale) => {
    return path.startsWith(locale.path)
  })
  return (locale ?? getDefaultLocale()) as LocaleConfig
}

// URL -> LocaleConfig
export function getLocaleFromURL(url: URL) {
  const pathname = url.pathname
  return getLocaleFromPath(pathname)
}

// "en-US" -> "/us/en"
export const getLocalePathFromLocaleISO = (localeCode?: string | null) => {
  if (!localeCode) return ''
  const [lang, country] = localeCode.split(/-|_/)
  return `/${country.toLowerCase()}/${lang.toLowerCase()}`
}

// "en-US" -> {country: 'US', lang: 'en'}
export const getLocalePartsFromLocaleISO = (localeCode?: string | null) => {
  if (!localeCode) return null
  const [lang, country] = localeCode.split(/-|_/)
  return { country: country.toUpperCase(), lang: lang.toLowerCase() }
}

export const getLocaleCodeFromCountryLang = (country: string, lang: string) => {
  const locale = Object.values(i18nConfig.locales).find(
    (locale) => locale.countryCodeLowercase === country && locale.lang === lang
  )
  return locale?.code ?? ''
}

export const getPreferredLanguage = (request: NextRequest) => {
  const headers = { 'accept-language': request.headers.get('accept-language') ?? 'en-US,en;q=0.5' }

  const languages = new Negotiator({
    headers,
  }).languages()

  // languages can be region specific or not as in:
  // [ 'en-US', 'en', 'pt' ]
  const preferredLanguage = languages[0].split('-')[0]

  return preferredLanguage
}

// "/us/en/some/path" -> "/us/en"
export const getPathLocale = (localizedPath: string | null) => {
  if (localizedPath === '/') return getDefaultLocale().path
  const locale = getLocaleFromPath(String(localizedPath))
  return locale.path
}

/** Returns the path without the locale. */
export const getUnlocalizedPath = (localizedPath: string) => {
  if (localizedPath === '/') return localizedPath
  const pathLocale = getPathLocale(localizedPath)
  const unlocalizedPath = localizedPath.replace(pathLocale ?? '', '')
  return unlocalizedPath
}

export const getPathFromUrl = (fullUrl?: string) => {
  if (!fullUrl) return ''
  const url = fullUrl.replace('http://', '').replace('https://', '')

  const firstSlashIndex = url.indexOf('/')

  const path = Array.from(url).slice(firstSlashIndex).join('')

  return path
}

// "/us/en" -> true, "/fr/fr" -> false
export function isValidLocalePath(localePath: string) {
  return Object.entries(i18nConfig.locales).some(([_, locale]) => {
    return localePath.startsWith(locale.path)
  })
}

// "/us/en" -> true, "/fr/fr" -> false
export function isValidCountry(localePath: string) {
  const country = localePath.split('/')[1].toLowerCase()
  return Object.entries(i18nConfig.locales).some(([_, locale]) => {
    return locale.countryCodeLowercase === country
  })
}

/**
 * Return the first English value found in a translated field.
 * To be used for analytics, reporting, or any case where we always want to send English.
 *
 * @example
 * **Input:**
 * ```
 * {
 *   "fr-CA": "Energize avant l’entraînement",
 *   "en-CA": "Energize Pre-Workout"
 * }
 * ```
 *
 * **Output:**
 * `'Energize Pre-Workout'`
 */
export function getEnglishFieldValue<T = string>(field: Record<string, T>) {
  try {
    const entries = Object.entries(field)
    const englishField = entries.find(([code]) => code.startsWith('en'))
    return englishField?.[1] ?? null
  } catch {
    return null
  }
}
